import React, { useMemo, useRef, useState } from 'react'
import { Avatar, Button, Image, notification, Space } from 'antd';
import CommonLayout from '../../../../layouts/Common'
import { useParams } from 'react-router-dom'
import { useCreateForumTopicComment, useGetForumTopic, useUpdateForumTopicComment } from '../../../../hooks/common/useForums';
import CustomLoading from '../../../../components/Common/Loading';
import { CategoryText, CommunityBody, CommunityCommentCard, CommunityCommentsContainer, CommunityTag, CommunityTagWrapper, Container, CoverImageWrapper, Title } from './style';
import { ForumTopicCommentType, TagsType } from '../../../../types';
import useTags from '../../../../hooks/common/useTags';
import imgPlaceholder from '../../../../assets/images/img-placeholder.png'
import { getRandomAvatarUrl } from '../../../../helpers';
import { AvatarContainer } from '../../../../layouts/Common/style';
import MaterialIcon from '../../../../components/Icons/MaterialIcon';
import { theme } from '../../../../styles/theme';
import Tiptap from '../../../../components/Common/Form/Tiptap';
import { StyledButton } from '../../../../components/Common/Button/style';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';
import { NotificationTitle } from '../../../../components/Common/Notification/style';
import { CheckCircleOutlined } from '@ant-design/icons';

const CommunityForumView = () => {
  const { forumId } = useParams();
  const [api, contextHolder] = notification.useNotification();
  const user = useSelector((state: RootState) => state.auth.user);
  const code = user && "code" in user ? user.code : null;

  const { data: tags, loading: loadingTags } = useTags();
  const { data: forumTopic, loading: loadingForumTopic, refetch: refetchForumTopic } = useGetForumTopic(forumId as string);
  const { create: createForumTopicComment, loading: loadingCreateForumTopicComment } = useCreateForumTopicComment();
  const { update: updateForumTopicComment, loading: loadingUpdateForumTopicComment } = useUpdateForumTopicComment();

  const tiptapRef = useRef<{ clearContent: () => void }>(null);
  const [description, setDescription] = useState<string>('');
  const [editCommentData, setEditCommentData] = useState<ForumTopicCommentType | null>(null);
  const communitySubGroupName = useMemo(() => (forumTopic?.attributes?.communitySubgroup?.attributes?.name), [forumTopic]);
  const communityModifiedComments = useMemo(() => {
    const modifiedData = forumTopic?.attributes?.comments?.map((comment: ForumTopicCommentType) => ({
      ...comment
    }))
    return modifiedData;
  }, [forumTopic]);

  const renderTag = (tagsArray: string[]) => {
    return tags
      ?.filter((tag: TagsType) => tagsArray.includes(tag.id))
      .map((tag: TagsType) => (
        <CommunityTag key={tag.id}>{tag.name}</CommunityTag>
      ));
  };

  const onEdit = (id: string) => {
    const commentData = communityModifiedComments?.filter((comment: ForumTopicCommentType) => comment.id === id)[0];
    setEditCommentData(commentData!);
  };

  const onSubmitComment = async () => {
    try {
      const res = await createForumTopicComment({ body: description, forumTopicId: forumId });
      console.log('onSubmitComment', res);
      if (res.success) {
        api.info({
          message: <NotificationTitle>Comment created successfully</NotificationTitle>,
          placement: 'topRight',
          icon:
            <CheckCircleOutlined
              style={{
                color: theme.color.primary,
              }}
            />,
          style: {
            background: `${theme.bg.secondary}`,
            borderRadius: 10,
            alignItems: 'center',
            display: 'flex'
          }
        });
      }
    } catch (err: any) {
      console.error('onSubmitComment', err);
    } finally {
      refetchForumTopic();
    }
  };

  const onUpdateComment = async () => {
    try {
      if (editCommentData) {
        const res = await updateForumTopicComment({ body: editCommentData.body, forumTopicId: editCommentData.id });
        console.log('onSubmitComment', res);
        if (res.success) {
          api.info({
            message: <NotificationTitle>Comment updated successfully</NotificationTitle>,
            placement: 'topRight',
            icon:
              <CheckCircleOutlined
                style={{
                  color: theme.color.primary,
                }}
              />,
            style: {
              background: `${theme.bg.secondary}`,
              borderRadius: 10,
              alignItems: 'center',
              display: 'flex'
            }
          });
        }
      }
    } catch (err: any) {
      console.error('onUpdateComment', err);
    } finally {
      refetchForumTopic();
    }
  };

  const onChangeDescription = (value: string) => {
    setDescription(value);
  };

  const onChangeCommentBody = (value: string) => {
    setEditCommentData((prev: any) => {
      if (prev) {
        return {
          ...prev,
          body: value
        }
      }
    })
  };

  console.log('editCommentData', editCommentData)

  if (loadingForumTopic || loadingTags) {
    return <CustomLoading />
  }

  return (
    <CommonLayout title={communitySubGroupName ?? ''} width="90%">
      {contextHolder}
      <Container>
        <Title>{forumTopic?.attributes?.title}</Title>
        <CategoryText>{forumTopic?.attributes?.category}</CategoryText>
        <CommunityTagWrapper>
          {renderTag(forumTopic?.attributes?.tags as string[])}
        </CommunityTagWrapper>
        <CoverImageWrapper>
          <Image
            height={350}
            src={forumTopic?.attributes.coverImageUrl ?? imgPlaceholder}
          />
        </CoverImageWrapper>
        <CommunityBody dangerouslySetInnerHTML={{ __html: forumTopic!.attributes.body }} />
        <CommunityCommentsContainer>
          {communityModifiedComments?.map((comment: ForumTopicCommentType) => (
            <CommunityCommentCard key={comment.id}>
              <div>
                <div className="avatar-wrapper">
                  <AvatarContainer>
                    <Avatar
                      src={comment?.professionalLogoUrl || getRandomAvatarUrl()}
                      size={{ xs: 60 }}
                    />
                  </AvatarContainer>
                </div>
                <div className="user-info">
                  <div>
                    <p className="owner-name">{comment?.owner}</p>
                    <span className="gray-color">•</span>
                    <p className="gray-color">{comment?.postedAt}</p>
                    {code === comment?.professionalCode && (
                      <Button
                        onClick={() => onEdit(comment?.id)}
                        type="text"
                        icon={
                          <MaterialIcon
                            icon="edit"
                            className="material-icons-sharp"
                            style={{ fontSize: 'inherit', color: theme.color.primary }}
                          />
                        }
                      />
                    )}
                  </div>
                  <div dangerouslySetInnerHTML={{ __html: comment?.body }}></div>
                </div>
              </div>
              <div className="edit-comment-input">
                {editCommentData?.id === comment?.id && (
                  <>
                    <Tiptap
                      ref={tiptapRef}
                      content={editCommentData?.body}
                      onChange={onChangeCommentBody}
                    />
                    <Space>
                      <StyledButton
                        onClick={() => setEditCommentData(null)}
                        loading={loadingUpdateForumTopicComment}
                        buttonType="secondary"
                      >
                        Cancel
                      </StyledButton>
                      <StyledButton
                        onClick={onUpdateComment}
                        loading={loadingUpdateForumTopicComment}
                        buttonType="primary"
                      >
                        Update
                      </StyledButton>
                    </Space>
                  </>
                )}
              </div>
            </CommunityCommentCard>
          ))}
        </CommunityCommentsContainer>
        <p>Comment</p>
        <Tiptap
          ref={tiptapRef}
          content={description}
          onChange={onChangeDescription}
        />
        <StyledButton
          onClick={onSubmitComment}
          loading={loadingCreateForumTopicComment}
          buttonType="primary"
        >
          Save
        </StyledButton>
      </Container>
    </CommonLayout>
  )
}

export default CommunityForumView